import { createApi } from "@reduxjs/toolkit/query/react";
import { baseProxyQueryWithReauth } from "./API";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase } = JSON.parse(
  el?.dataset?.page ?? ""
).props;

export type ContactType = {
  uuid: string;
  status: "active" | "inactive";
  createdAt: string;
  createdNow: boolean;
  details: any[];
  contactSets: any[];
};

export type SaveImportedContactsOptionsType = {
  fileUuid: string;
  matched?: boolean;
  notMatched?: boolean;
  rowStart?: number;
  rowEnd?: number;
  validOnly: boolean;
  createNewFields?: any[];
  ignoreFieldUuids?: string[];
  doNotCreateNewOptions?: string[];
};

// Define a service using a base URL and expected endpoints
export const contactsAPI = createApi({
  reducerPath: "contactsAPI",
  baseQuery: baseProxyQueryWithReauth("contacts/v1"),
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "Contacts",
    "ContactFields",
    "ContactSets",
    "TempRecords",
    "TempFiles",
    "ExportFiles",
  ],
  endpoints: (builder) => ({
    getContactFields: builder.query<any, void>({
      query: () => `fields`,
      transformResponse: (response: { fields: any }, meta, arg) =>
        response.fields,
      providesTags: ["ContactFields"],
    }),
    upsertContactFields: builder.mutation<any, any>({
      query: (body) => ({
        url: `upsert-fields`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ContactFields"],
    }),
    upsertField: builder.mutation<any, any>({
      query: (body) => ({
        url: `upsert-field`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ContactFields"],
    }),
    getContact: builder.query<
      ContactType,
      { contactUuid: string; isAllFields?: boolean; hideToasts?: boolean }
    >({
      query: ({ contactUuid, isAllFields, hideToasts }) => ({
        url: `first/${contactUuid}`,
        params: { "fields.all": isAllFields ? "1" : "" },
        hideToasts,
      }),
      providesTags: ["Contacts"],
    }),
    getFieldsForImportedFile: builder.query<any, string>({
      query: (uuid) => `columns-for-import-temp-records/${uuid}`,
    }),
    saveImportedContacts: builder.mutation<
      any,
      SaveImportedContactsOptionsType
    >({
      query: (body) => ({
        url: `import-temp-records`,
        method: "POST",
        body,
      }),
    }),
    getContacts: builder.query<
      { contacts: ContactType[]; pagination: { total: number } },
      { page: number; perPage: number; search: string }
    >({
      query: ({ page, perPage, search }) => ({
        url: `find`,
        method: "POST",
        body: {
          fields: {
            cards: true,
            channels: true,
            uuids: [],
          },
          filters: [
            {
              fields: [],
              formats: ["card", "primary"],
              is_or: true,
              operator: "prefix",
              order: 0,
              values: [],
            },
            {
              fields: ["status"],
              formats: [],
              isOr: false,
              offset: "0",
              offsetFrom: "fixed",
              offsetType: "positive",
              offsetUnit: "day",
              operator: "is",
              order: 0,
              thread: "CurrentThread",
              timeZone: "",
              timeZoneFrom: "fixed",
              valueId: "",
              values: ["active"],
              valueType: "fixed",
            },
          ],
          paginate: {
            order: "desc",
            order_by: "created_at",
            page,
            perPage,
          },
        },
      }),
      providesTags: ["Contacts"],
    }),
    upsertOneField: builder.mutation<any, any>({
      query: (body) => ({
        url: `upsert-one-field`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Contacts"],
    }),
    getContactSets: builder.query<
      any,
      {
        search?: string;
        page?: number;
        perPage?: number;
        isStatic?: boolean;
        uuid?: string[];
        excludeUuids?: string[];
      }
    >({
      query: (params) => ({
        url: `contact-sets`,
        params: {
          search_label: params.search,
          "paginate.page": params.page,
          "paginate.per_page": params.perPage,
          ...(params.isStatic ? { with_type: "static" } : {}),
          ...(params.uuid ? { uuid: params.uuid } : {}),
          ...(params.excludeUuids
            ? { exclude_uuids: params.excludeUuids }
            : {}),
        },
      }),
      providesTags: ["ContactSets"],
    }),
    createContactSet: builder.mutation<
      any,
      {
        uuid: string;
        label: string;
        filters?: any[];
        type?: "static" | "dynamic";
      }
    >({
      query: (body) => ({
        url: `contact-set`,
        method: "POST",
        body: {
          contact_set: {
            uuid: body.uuid,
            label: body.label,
            type: body.type || "static",
            filters: body.filters || [],
          },
        },
      }),
      invalidatesTags: ["ContactSets"],
    }),
    findMatchingContactFields: builder.query<
      any,
      {
        is_sms: boolean;
        is_voice: boolean;
        is_primary: boolean;
        date_time_type: string;
      }
    >({
      query: (body) => ({
        url: `find-matching-fields`,
        method: "POST",
        body,
      }),
    }),
    getTempFiles: builder.query<
      any,
      {
        search?: string;
        orderBy?: string;
        order?: string;
        status?: string;
        page?: number;
        perPage?: number;
        mustHeaders?: string[];
      }
    >({
      query: ({
        search = "",
        orderBy = "created",
        order = "desc",
        status = "",
        page = 0,
        perPage = 10,
        mustHeaders = [],
      }) => {
        let url = `temp-files?search=${search}&pagination.order_by=${orderBy}&pagination.order=${order}&status=${status}&pagination.page=${page}&pagination.per_page=${perPage}`;
        url += mustHeaders.map((h) => `&must_header=${h}`).join("");
        return url;
      },
      providesTags: ["TempFiles"],
    }),
    getTempFileInfo: builder.query<any, string>({
      query: (uuid) => `temp-files/${uuid}`,
    }),
    getTempRecords: builder.query<
      any,
      {
        file: string;
        channels?: string[];
        withContacts?: boolean;
        withDuplicatedRows?: boolean;
        withDuplicatedContacts?: boolean;
        duplicatedIndex?: number;
        search?: string;
        page?: number;
        perPage?: number;
      }
    >({
      query: ({
        file,
        channels = [],
        withContacts,
        withDuplicatedRows,
        withDuplicatedContacts,
        duplicatedIndex,
        search,
        page = 0,
        perPage = 12,
      }) => {
        let url = `temp-records?file=${file}`;
        url += channels.map((c) => `&channels=${c}`).join("");
        if (withContacts) url += "&with.contacts=yes";
        if (withDuplicatedRows) url += "&with.duplicated_rows=yes";
        if (withDuplicatedContacts) url += "&with.duplicated_contacts=yes";
        if (search) url += `&search=${search}`;
        url += `&paginate.page=${page}&paginate.per_page=${perPage}`;
        return {
          url,
          hideToasts: true,
        };
      },
      providesTags: ["TempRecords"],
    }),
    uploadCsv: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "upload-csv",
        method: "POST",
        body: formData,
      }),
    }),
    processTempFile: builder.mutation<
      any,
      {
        file: string;
        idHeaders: string[];
        headers: string[];
        defaultCountryIso: string;
        parseForImport?: boolean;
      }
    >({
      query: ({
        file,
        idHeaders,
        headers,
        defaultCountryIso,
        parseForImport,
      }) => {
        let url = `process-one-time-import-file?file=${file}`;
        url += idHeaders.map((h) => `&id_headers=${h}`).join("");
        url += headers.map((h) => `&headers=${h}`).join("");
        url += `&default_country_iso=${defaultCountryIso}`;
        if (parseForImport) url += `&parse_for_import=1`;
        return { url, method: "GET" };
      },
    }),
    updateTempRecord: builder.mutation<
      any,
      { file: string; rowIndex: number[]; data: any }
    >({
      query: (body) => ({
        url: "temp-records-update",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TempRecords"],
    }),
    deleteTempRecords: builder.mutation<
      any,
      {
        file: string;
        rowIndexes?: number[];
        withContacts?: boolean;
        withDuplicatedRows?: boolean;
        withDuplicatedContacts?: boolean;
      }
    >({
      query: ({
        file,
        rowIndexes,
        withContacts,
        withDuplicatedRows,
        withDuplicatedContacts,
      }) => {
        let url = `temp-records?file=${file}`;
        if (rowIndexes?.length)
          url += rowIndexes.map((r) => `&row_index=${r}`).join("");
        if (withContacts) url += "&with.contacts=yes";
        if (withDuplicatedRows) url += "&with.duplicated_rows=yes";
        if (withDuplicatedContacts) url += "&with.duplicated_contacts=yes";
        return { url, method: "DELETE" };
      },
      invalidatesTags: ["TempRecords"],
    }),
    deleteAllContacts: builder.mutation<any, void>({
      query: () => ({
        url: "delete-all-contacts",
        method: "GET",
      }),
      invalidatesTags: ["Contacts", "ContactSets", "TempRecords"],
    }),
    exportContacts: builder.mutation<any, void>({
      query: () => ({
        url: "export-contacts",
        method: "GET",
      }),
      invalidatesTags: ["ExportFiles"],
    }),
    getExportFiles: builder.query<any, void>({
      query: () => "export-files",
      providesTags: ["ExportFiles"],
    }),
    deleteExportFiles: builder.mutation<any, string[]>({
      query: (uuids) => {
        let url = "delete-export-files";
        if (uuids?.length) {
          url += `?${uuids
            .map((uuid) => `uuid=${encodeURIComponent(uuid)}`)
            .join("&")}`;
        }
        return {
          url,
          method: "GET",
        };
      },
      invalidatesTags: ["ExportFiles"],
    }),
    deleteTempFiles: builder.mutation<any, string[]>({
      query: (uuids) => {
        let url = "delete-temp-files";
        if (uuids?.length) {
          url += `?${uuids
            .map((uuid) => `uuid=${encodeURIComponent(uuid)}`)
            .join("&")}`;
        }
        return {
          url,
          method: "GET",
        };
      },
      invalidatesTags: ["TempFiles"],
    }),
    downloadExportFile: builder.query<Blob, string>({
      query: (fileUuid) => ({
        url: `download-export-file/${fileUuid}`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetContactFieldsQuery,
  useGetContactQuery,
  useUpsertContactFieldsMutation,
  useUpsertFieldMutation,
  useUpsertOneFieldMutation,
  useGetContactsQuery,
  useGetContactSetsQuery,
  useCreateContactSetMutation,
  useFindMatchingContactFieldsQuery,
  useLazyFindMatchingContactFieldsQuery,
  useGetFieldsForImportedFileQuery,
  useSaveImportedContactsMutation,
  useGetTempFilesQuery,
  useLazyGetTempFilesQuery,
  useGetTempFileInfoQuery,
  useLazyGetTempFileInfoQuery,
  useGetTempRecordsQuery,
  useLazyGetTempRecordsQuery,
  useUploadCsvMutation,
  useProcessTempFileMutation,
  useUpdateTempRecordMutation,
  useDeleteTempRecordsMutation,
  useDeleteAllContactsMutation,
  useExportContactsMutation,
  useGetExportFilesQuery,
  useLazyGetExportFilesQuery,
  useDeleteExportFilesMutation,
  useDeleteTempFilesMutation,
  useDownloadExportFileQuery,
  useLazyDownloadExportFileQuery,
} = contactsAPI;
